import { useContext } from "react";

import useTranslation from "next-translate/useTranslation";

import CollectionContext from "modules/Collection/context";

import contrastAutoAdjust from "shared/utils/contrastAutoAdjust";
import { useFeature } from "shared/utils/hooks/useFeature";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";

import { StarFilled, StarOutlined } from "@ant-design/icons";

import { StarWithText } from "./styles";

type CollectionList = {
  resources: [
    {
      relation: {
        id: string;
        className: string;
      };
    },
  ];
};

type Collection = [
  {
    id: string;
    className: string;
  } | null,
];

type CheckStarProps = {
  handleVisible: () => {};
  className: string;
  type: string;
  textEnabled: boolean;
  autoContrastEnable: boolean;
  entityId: string | number;
};

const CheckStar = ({
  type,
  entityId,
  className,
  textEnabled,
  handleVisible,
  autoContrastEnable,
}: CheckStarProps): JSX.Element => {
  const isDevelopmentEnvironment = process.env.NODE_ENV === "development";

  if (isDevelopmentEnvironment) {
    if (!type) throw new Error("Type attribute is required");

    if (!entityId) throw new Error("EntityId attribute is required");

    if (!className) throw new Error("ClassName attribute is required");
  }

  const { t } = useTranslation();
  const { state } = useGlobalContext();
  const { collections, setCollectionType, newContentAdded } =
    useContext(CollectionContext);
  const isCollectionsEnabled = useFeature("f_collections");

  const collectionsList = collections;
  const settings = state.tenancy?.settings;
  const isDarkMode = state.theme.value === "dark";
  const tenantTheme = {
    ...settings?.["general-theme"],
    ...settings?.["advanced-theme"],
  };

  const headerBg =
    tenantTheme.themeHeaderStyle === "automatic"
      ? tenantTheme.themeMasterColor
      : tenantTheme.themeHeaderBackgroundColor;

  const starColor = autoContrastEnable
    ? contrastAutoAdjust({
        background: isDarkMode ? "#404040" : headerBg,
        text: "#FFA800",
      })
    : "#FFA800";

  const items: Collection = [null];
  collectionsList?.map((collection: CollectionList) => {
    if (collection?.resources.length)
      collection.resources.map((resource) =>
        items.push({
          id: resource.relation.id,
          className: resource.relation.className,
        }),
      );
    return null;
  });

  if (settings.collections.colletionsActive === "0") return null;

  if (!isCollectionsEnabled) return null;

  const classList: any = {
    course: "Vico068\\CourseTools\\Course",
    lessons: "Vico068\\CourseTools\\Lesson",
    media_space: "App\\MediaSpaceContent",
    blog: "App\\Blog",
    topics: "Deskti\\Forum\\Models\\ForumTopic",
  };

  if (
    newContentAdded?.className === classList[type] &&
    newContentAdded?.items[0] === entityId
  ) {
    return (
      <>
        {!textEnabled ? (
          <StarFilled
            onClick={() => {
              handleVisible();
              setCollectionType(type, entityId);
            }}
            className="star-icon"
            data-testid="star-icon"
            style={{ color: starColor }}
          />
        ) : (
          <StarWithText
            onClick={() => {
              handleVisible();
              setCollectionType(type, entityId);
            }}
          >
            <StarFilled
              className="star-icon"
              data-testid="star-icon"
              style={{ color: starColor }}
            />

            {t("common:addedToCollection")}
          </StarWithText>
        )}
      </>
    );
  }

  if (
    ![...new Set(items)].some(
      (item) => item?.id === entityId && item.className === className,
    )
  ) {
    return (
      <>
        {!textEnabled ? (
          <StarOutlined
            onClick={() => {
              handleVisible();
              setCollectionType(type, entityId);
            }}
            className="star-icon"
            data-testid="star-icon"
            style={{ color: starColor }}
          />
        ) : (
          <StarWithText
            onClick={() => {
              handleVisible();
              setCollectionType(type, entityId);
            }}
          >
            <StarOutlined
              onClick={() => {
                handleVisible();
                setCollectionType(type, entityId);
              }}
              className="star-icon"
              data-testid="star-icon"
              style={{ color: starColor }}
            />

            {t("common:addThisItemToCollection")}
          </StarWithText>
        )}
      </>
    );
  }

  const ids = new Set();
  const unique = [...new Set(items)].filter((obj) => {
    if (ids.has(`${obj?.className} - ${obj?.id}`)) return false;

    ids.add(`${obj?.className} - ${obj?.id}`);
    return true;
  });

  return items.length ? (
    <>
      {[...new Set(unique)].map((item) => {
        if (item?.id === entityId && item.className === className) {
          return (
            <>
              {!textEnabled ? (
                <StarFilled
                  key={item.id}
                  onClick={() => {
                    handleVisible();
                    setCollectionType(type, entityId);
                  }}
                  className="star-icon"
                  data-testid="star-icon"
                  style={{ color: starColor }}
                />
              ) : (
                <StarWithText
                  onClick={() => {
                    handleVisible();
                    setCollectionType(type, entityId);
                  }}
                >
                  <StarFilled
                    className="star-icon"
                    data-testid="star-icon"
                    style={{ color: starColor }}
                  />

                  {t("common:addedToCollection")}
                </StarWithText>
              )}
            </>
          );
        }

        return null;
      })}
    </>
  ) : (
    <>
      {!textEnabled ? (
        <StarOutlined
          onClick={() => {
            handleVisible();
            setCollectionType(type, entityId);
          }}
          className="star-icon"
          data-testid="star-icon"
          style={{ color: starColor }}
        />
      ) : (
        <StarWithText
          onClick={() => {
            handleVisible();
            setCollectionType(type, entityId);
          }}
        >
          <StarOutlined
            className="star-icon"
            data-testid="star-icon"
            style={{ color: starColor }}
          />

          {t("common:addThisItemToCollection")}
        </StarWithText>
      )}
    </>
  );
};

CheckStar.defaultProps = {
  entityId: null,
  textEnabled: false,
  autoContrastEnable: false,
};

export default CheckStar;
